import { useCallback, useEffect, useMemo } from 'react';
import { Button, Container, Grid, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';
import {
  getBankDropdownData,
  getLoanTypeData,
  getCityData,
  getSourceListData,
  getStateData,
  setCityData
} from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { addProspectData } from '../../store/Slice/Prospect/ProspectSlice';

function AddEditProspect() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rowId } = useParams();
  const { stateDrop, CityDrop, LoanTypeDrop, BankDrop, SourceDrop } = useGlobalSelector();
  const { LoginData } = useLoginSelector();

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      mobile: '',
      customerName: '',
      customerEmail: '',
      provinceId: '',
      districtId: '',
      loanType: '',
      loanAmountRequested: '',
      bankPref: '',
      sourceTypeId: ''
    }
  });

  const formFields = useMemo(
    () => [
      {
        name: 'mobile',
        label: 'Mobile',
        type: 'Number',
        isInput: true,
        code: 'MOBILE',
        fType: 'NUMBER_FIELD'
      },
      {
        name: 'customerName',
        label: 'Name',
        type: 'Text',
        isInput: true,
        code: 'NAME',
        fType: 'ALPHABET'
      },
      {
        name: 'customerEmail',
        label: 'Email',
        type: 'text',
        isInput: true,
        code: 'EMAIL',
        fType: 'TEXT_FIELD'
      },
      {
        name: 'provinceId',
        label: 'State',
        isInput: false,
        options: stateDrop,
        onChange: (val) => {
          dispatch(setCityData([]));
          setValue('districtId', null);
          if (val) dispatch(getCityData({ stateId: val }));
        }
      },
      { name: 'districtId', label: 'City', isInput: false, options: CityDrop },
      { name: 'loanType', label: 'Loan Type', isInput: false, options: LoanTypeDrop },
      {
        name: 'loanAmountRequested',
        label: 'Loan Amount',
        type: 'Number',
        isInput: true,
        code: 'AMOUNT',
        fType: 'AMOUNT_FIELD'
      },
      { name: 'bankPref', label: 'Bank Preference', isInput: false, options: BankDrop },
      { name: 'sourceTypeId', label: 'Source', isInput: false, options: SourceDrop }
    ],
    [stateDrop, CityDrop, LoanTypeDrop, BankDrop, SourceDrop, dispatch, setValue]
  );

  const onSubmit = useCallback(
    async (data) => {
      const res = await dispatch(
        addProspectData({ ...data, companyId: LoginData?.companyId, userId: LoginData?.userId })
      );
      if (res?.status === 'success') navigate('/prospect');
    },
    [LoginData, dispatch, navigate]
  );

  useEffect(() => {
    dispatch(getStateData({ countryId: 101 }));
    dispatch(getSourceListData());
    if (LoginData) {
      const creds = { companyId: LoginData.companyId, userId: LoginData.userId };
      dispatch(getLoanTypeData(creds));
      dispatch(getBankDropdownData(creds));
    }
  }, [dispatch, LoginData]);

  return (
    <Page title="Kpros by Kalolytic Solutions || Prospect">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info={rowId ? 'Edit Prospect' : 'Add New Prospect'} />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            Fill Details
          </Typography>
          <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
            <form id="prospectForm" onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3} mb={3}>
                {formFields.map((f) => (
                  <Grid item xs={4} key={f.name}>
                    {f.isInput ? (
                      <CustomInput
                        name={f.name}
                        label={f.label}
                        type={f.type}
                        control={control}
                        errors={errors}
                        isRequired
                        fieldCode={f.code}
                        fieldType={f.fType}
                      />
                    ) : (
                      <CustomSelect
                        name={f.name}
                        label={f.label}
                        control={control}
                        errors={errors}
                        options={f.options || []}
                        isRequired
                        onChange={f.onChange ? (e, v) => f.onChange(v) : undefined}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
            </form>
          </Box>
          <Box textAlign="right">
            <Button type="submit" form="prospectForm" variant="contained" className="mainBtn">
              Save
            </Button>
          </Box>
        </Box>
      </Container>
    </Page>
  );
}

export default AddEditProspect;
